import React, { ReactNode } from 'react'
import { VIEWED_SESSIONS_KEY } from '../constants/localStorageKeys'
import { getFromStorage, setToStorage } from '../utils/storageUtils'

export interface ViewedSession {
  sessionId: string
  viewedAt: string
}

export interface HistoryContextState {
  historyState: {
    viewedSessions: ViewedSession[]
  }
  historyReducer: {
    addViewedSession: (sessionId: string) => void
    pruneViewedSessions: () => void
    hasViewedSession: (sessionId: string) => boolean
  }
}

const HistoryContext = React.createContext<HistoryContextState | undefined>(undefined)

export function HistoryProvider({ children }: { children: ReactNode }) {
  // Load initial state from localStorage
  const storedItems = getFromStorage<ViewedSession[]>(VIEWED_SESSIONS_KEY, true) ?? []

  const [viewedSessions, setViewedSessions] = React.useState<ViewedSession[]>(storedItems)

  // Maintain a Set for O(1) sessionId lookups
  const [viewedSessionIds, setViewedSessionIds] = React.useState<Set<string>>(() => {
    return new Set(storedItems.map((s: ViewedSession) => s.sessionId))
  })

  // Save to localStorage whenever viewedSessions changes
  React.useEffect(() => {
    try {
      setToStorage<ViewedSession[]>(VIEWED_SESSIONS_KEY, viewedSessions)
    } catch (error) {
      console.error('Failed to save viewed sessions to localStorage:', error)
    }
  }, [viewedSessions])

  const addViewedSession = (sessionId: string) => {
    // Use Set for O(1) lookup instead of array.some()
    if (viewedSessionIds.has(sessionId)) {
      return
    }

    const session: ViewedSession = {
      sessionId,
      viewedAt: new Date().toISOString(),
    }

    setViewedSessions((prev) => [session, ...prev])
    setViewedSessionIds((prev) => new Set([...prev, sessionId]))
  }

  const pruneViewedSessions = () => {
    const cutoffDate = new Date()
    cutoffDate.setDate(cutoffDate.getDate() - 10)

    setViewedSessions((prev) => {
      const filtered = prev.filter((session) => new Date(session.viewedAt) > cutoffDate)
      // Update the Set to match the filtered array
      setViewedSessionIds(new Set(filtered.map(s => s.sessionId)))
      return filtered
    })
  }

  const hasViewedSession = (sessionId: string) => {
    return viewedSessionIds.has(sessionId)
  }

  const state: HistoryContextState = React.useMemo(() => {
    return {
      historyState: {
        viewedSessions,
      },
      historyReducer: {
        addViewedSession,
        pruneViewedSessions,
        hasViewedSession,
      },
    }
  }, [viewedSessions, addViewedSession, pruneViewedSessions])

  return <HistoryContext.Provider value={state}>{children}</HistoryContext.Provider>
}

export function useHistoryContext() {
  const context = React.useContext(HistoryContext)
  if (!context) {
    throw new Error('useHistoryContext must be used within a HistoryProvider')
  }
  return context
}
